/**
 * Printable workspace item preview — configurable fields, fixed paper presets, `@page`-aware layout.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { useItemsStore } from '@/stores/prioritization/itemsStore';
import { isUuid } from '@/lib/isUuid';
import { useEnterpriseIdentityStore } from '@/stores/prioritization/enterpriseIdentityStore';
import type { PrintPaperId } from '@/lib/print/paperPresets';
import {
  PRINT_PAPER_PRESETS,
  getPrintPaperPreset,
  loadSavedPrintPaperId,
  savePrintPaperId,
} from '@/lib/print/paperPresets';
import {
  ITEM_PRINT_FIELD_IDS,
  ITEM_PRINT_FIELD_LABELS,
  loadItemPrintVisibility,
  saveItemPrintVisibility,
  type ItemPrintFieldVisibility,
  type ItemPrintFieldId,
} from '@/lib/print/itemPrintPreferences';
import { PrintChrome } from '@/components/print/PrintChrome';
import { ItemPrintBody } from '@/components/print/ItemPrintBody';
import { ItemOnePagerBody } from '@/components/print/ItemOnePagerBody';

type PrintTemplateId = 'standard' | 'one-pager';

const STORAGE_KEY_TEMPLATE = 'priorities-ai:item-print-template-v1';

function loadSavedTemplateId(): PrintTemplateId {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_TEMPLATE);
    if (raw === 'standard' || raw === 'one-pager') return raw;
  } catch {
    /* ignore */
  }
  return 'standard';
}

function saveTemplateId(id: PrintTemplateId): void {
  try {
    localStorage.setItem(STORAGE_KEY_TEMPLATE, id);
  } catch {
    /* ignore */
  }
}

export function ItemPrintPage() {
  const { itemId } = useParams<{ itemId: string }>();
  const getItemById = useItemsStore((s) => s.getItemById);
  const loadItemById = useItemsStore((s) => s.loadItemById);
  const storeItem = useItemsStore((s) => itemId ? s.getItemById(itemId) : null);

  const orgUnits = useEnterpriseIdentityStore((s) => s.orgUnits);
  const [loading, setLoading] = useState(true);
  const [paperId, setPaperId] = useState<PrintPaperId>(() => loadSavedPrintPaperId());
  const [visibility, setVisibility] = useState<ItemPrintFieldVisibility>(() => loadItemPrintVisibility());
  const [templateId, setTemplateId] = useState<PrintTemplateId>(() => loadSavedTemplateId());

  const handleTemplateChange = useCallback((id: PrintTemplateId) => {
    setTemplateId(id);
    saveTemplateId(id);
  }, []);

  useEffect(() => {
    if (!itemId) {
      setLoading(false);
      return;
    }
    if (getItemById(itemId)) {
      setLoading(false);
      return;
    }
    // Non-UUID segments can never be a DB item — skip the doomed fetch.
    if (!isUuid(itemId)) {
      setLoading(false);
      return;
    }
    setLoading(true);
    loadItemById(itemId).finally(() => setLoading(false));
  }, [itemId, getItemById, loadItemById]);

  const handleToggle = useCallback((field: ItemPrintFieldId, checked: boolean) => {
    setVisibility((prev) => {
      const next = { ...prev, [field]: checked };
      saveItemPrintVisibility(next);
      return next;
    });
  }, []);

  useEffect(() => {
    savePrintPaperId(paperId);
  }, [paperId]);

  const preset = useMemo(() => getPrintPaperPreset(paperId), [paperId]);

  const queueLabel = useMemo(() => {
    if (!storeItem) return undefined;
    const qid =
      (storeItem as { queueId?: string; queue_id?: string }).queueId ??
      (storeItem as { queue_id?: string }).queue_id ??
      null;
    if (!qid) return undefined;
    const ou = orgUnits.get(qid);
    return ou?.displayName ?? ou?.name ?? qid;
  }, [storeItem, orgUnits]);

  const docTitle =
    storeItem?.title?.trim() ? `${storeItem.title.trim()} — Print` : itemId ? `Item ${itemId.slice(0, 8)}… — Print` : 'Print';

  if (!itemId) {
    return (
      <div className="p-8 text-center text-sm text-[var(--text-sec)]">
        <p>No item selected.</p>
      </div>
    );
  }

  if (loading && !storeItem) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center gap-2">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--text-ter)]" />
        <span className="text-[var(--text-ter)]">Loading item…</span>
      </div>
    );
  }

  if (!storeItem) {
    return (
      <div className="p-8 text-center">
        <p className="text-sm text-[var(--text-sec)]">Item not found.</p>
      </div>
    );
  }

  return (
    <PrintChrome
      documentTitle={docTitle}
      pageSizeCss={preset.pageSizeCss}
      previewMaxWidthMm={preset.previewMaxWidthMm}
      backHref={`/prioritization/items/${itemId}`}
      backLabel="Return to item"
      pagedRenderKey={`${itemId}|${String(storeItem.updatedAt)}|${paperId}|${templateId}|${JSON.stringify(visibility)}`}
      toolbarAside={
        <div className="flex flex-wrap items-center gap-3 print:hidden">
          {/* Template toggle */}
          <div className="flex items-center border border-[var(--rule)] overflow-hidden text-xs shadow-sm">
            <button
              type="button"
              onClick={() => handleTemplateChange('standard')}
              className={`px-3 py-1.5 font-medium transition-colors ${
                templateId === 'standard'
                  ? 'bg-[var(--bg)] text-white'
                  : 'bg-[var(--bg-raised)] text-[var(--text-sec)] hover:bg-[var(--surface)]'
              }`}
            >
              Standard
            </button>
            <button
              type="button"
              onClick={() => handleTemplateChange('one-pager')}
              className={`px-3 py-1.5 font-medium border-l border-[var(--rule)] transition-colors ${
                templateId === 'one-pager'
                  ? 'bg-[var(--bg)] text-white'
                  : 'bg-[var(--bg-raised)] text-[var(--text-sec)] hover:bg-[var(--surface)]'
              }`}
            >
              One-pager
            </button>
          </div>

          <label className="flex items-center gap-2 text-xs text-[var(--text-sec)]">
            <span>Paper size</span>
            <select
              value={paperId}
              onChange={(e) => setPaperId(e.target.value as PrintPaperId)}
              className="border border-[var(--rule)] bg-[var(--bg-raised)] px-2 py-1.5 text-xs text-[var(--text-pri)] shadow-sm"
            >
              {PRINT_PAPER_PRESETS.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>

          {templateId === 'standard' && (
            <div className="max-h-[8.5rem] w-full sm:w-auto sm:max-w-[26rem] overflow-y-auto border border-[var(--rule)] bg-[var(--surface)] px-3 py-2 shadow-inner">
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-[var(--text-ter)]">
                Visible fields{" "}
                <span className="font-normal text-[var(--text-ter)] normal-case">
                  — saved in this browser; title is shown as page heading only
                </span>
              </p>
              <div className="grid grid-cols-1 gap-x-4 gap-y-0.5 sm:grid-cols-2">
                {ITEM_PRINT_FIELD_IDS.map((fid) => (
                  <label
                    key={fid}
                    className="inline-flex cursor-pointer items-center gap-2 px-1 py-0.5 hover:bg-[var(--bg-raised)]"
                  >
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 rounded border-[var(--rule)] text-[var(--text-pri)]"
                      checked={visibility[fid]}
                      onChange={(ev) => handleToggle(fid, ev.target.checked)}
                    />
                    <span className="text-xs text-[var(--text-sec)]">{ITEM_PRINT_FIELD_LABELS[fid]}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      }
    >
      {templateId === 'one-pager' ? (
        <ItemOnePagerBody item={storeItem} queueLabel={queueLabel} />
      ) : (
        <ItemPrintBody item={storeItem} visibility={visibility} queueLabel={queueLabel} />
      )}
    </PrintChrome>
  );
}
